/**
 * Export scorecard and detailed analysis to text or Word document.
 */

import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  AlignmentType,
  convertInchesToTwip,
} from 'docx';
import type { AnalysisResults, Blueprint, NucliusScorecardSections } from '../types/analysis';
import { hasDeterministicPillarScores, isNucliusBlueprint } from '../types/analysis';
import {
  formatOriginalityDisplay,
  isOriginalityExcluded,
  originalityExclusionNotice,
} from './originalityDisplay';

/** Strip markdown bold (**text**) to plain text. */
function stripBold(text: string): string {
  return text.replace(/\*\*(.+?)\*\*/g, '$1');
}

/** Split genre analysis into content pacing and genre accuracy sections. */
function splitGenreAnalysis(raw: string | undefined): { contentPacing: string; genreAccuracy: string } {
  const text = typeof raw === 'string' ? raw : '';
  const pacingIdx = text.search(/\*\*CONTENT PACING\*\*/i);
  const genreIdx = text.search(/\*\*GENRE ACCURACY\*\*/i);
  if (pacingIdx !== -1 && genreIdx !== -1 && genreIdx > pacingIdx) {
    return {
      contentPacing: stripBold(text.slice(pacingIdx, genreIdx).replace(/^\*\*CONTENT PACING\*\*\s*/i, '')).trim(),
      genreAccuracy: stripBold(text.slice(genreIdx).replace(/^\*\*GENRE ACCURACY\*\*\s*/i, '')).trim(),
    };
  }
  if (pacingIdx !== -1) {
    const after = stripBold(text.slice(pacingIdx).replace(/^\*\*CONTENT PACING\*\*\s*/i, '')).trim();
    return { contentPacing: after, genreAccuracy: text.slice(0, pacingIdx).trim() || after };
  }
  if (genreIdx !== -1) {
    const after = stripBold(text.slice(genreIdx).replace(/^\*\*GENRE ACCURACY\*\*\s*/i, '')).trim();
    return { contentPacing: text.slice(0, genreIdx).trim() || after, genreAccuracy: after };
  }
  return { contentPacing: stripBold(text), genreAccuracy: stripBold(text) };
}

function fmtPct(val: number | null | undefined): string {
  if (val == null || Number.isNaN(Number(val))) return '—';
  return `${(Math.round(Number(val) * 10) / 10).toFixed(1)}%`;
}

const NUCLIUS_EXPORT_KEYS: (keyof NucliusScorecardSections)[] = [
  'story_overview',
  'mandate',
  'engagement',
  'commerciality',
  'recommendation',
];

const NUCLIUS_SECTION_TITLES: Record<string, string> = {
  story_overview: 'Story Overview',
  mandate: 'Mandate',
  engagement: 'Engagement',
  commerciality: 'Commerciality',
  recommendation: 'Recommendation',
};

function getNucliusSections(results: AnalysisResults): NucliusScorecardSections | null {
  const bp = results.blueprint as Blueprint | null | undefined;
  if (!isNucliusBlueprint(bp)) return null;
  return bp?.sections ?? null;
}

export type NucliusExportOptions = {
  includeTechnicalAppendix?: boolean;
};

/** Build the concise five-section report; diagnostics are explicitly opt-in. */
export function buildNucliusScorecardText(
  results: AnalysisResults,
  options: NucliusExportOptions = {},
): string {
  const sections = getNucliusSections(results);
  if (!sections) return '';
  const bp = results.blueprint as Blueprint;
  const label =
    bp?.commission_summary ??
    bp?.sections_structured?.recommendation?.recommendation_label ??
    '—';
  const title = results.script_title || 'Script Analysis';
  const lines: string[] = [
    'NUCLIUS INDEX REPORT',
    '='.repeat(20),
    '',
    title,
    `Final Commissioning Recommendation: ${label}`,
    '',
  ];
  for (const key of NUCLIUS_EXPORT_KEYS) {
    const md = sections[key]?.trim();
    if (!md) continue;
    lines.push('-'.repeat(50));
    lines.push(NUCLIUS_SECTION_TITLES[key] ?? key);
    lines.push('-'.repeat(50));
    lines.push('');
    lines.push(md);
    lines.push('');
  }
  if (options.includeTechnicalAppendix && sections.full?.trim()) {
    lines.push('-'.repeat(50));
    lines.push('Full Analysis Output (Appendix)');
    lines.push('-'.repeat(50));
    lines.push('');
    lines.push(sections.full.trim());
    lines.push('');
  }
  return lines.join('\n');
}

/** Build plain text representation of the full scorecard. */
export function buildScorecardText(
  results: AnalysisResults,
  options: NucliusExportOptions = {},
): string {
  const nucliusText = buildNucliusScorecardText(results, options);
  if (nucliusText.trim()) {
    return `${nucliusText.trim()}\n`;
  }
  const bm =
    results.blueprint_markdown ||
    (results.blueprint && typeof results.blueprint === 'object' && 'blueprint_markdown' in results.blueprint
      ? (results.blueprint as { blueprint_markdown?: string }).blueprint_markdown
      : undefined);
  if (typeof bm === 'string' && bm.trim()) {
    return `${bm.trim()}\n`;
  }
  const scorecard = results.scorecard;
  const isCreator = results.audience_type === 'creator';
  const scorecardTitle = 'SCRIPT ANALYSIS RESULT';
  const useDeterministicScores = scorecard ? hasDeterministicPillarScores(scorecard) : false;
  const vs = scorecard?.v7_scores ?? scorecard?.v6_scores;

  const lines: string[] = [];

  lines.push(scorecardTitle);
  lines.push('='.repeat(scorecardTitle.length));
  lines.push('');
  lines.push(results.script_title || 'Script Analysis');
  if (scorecard?.main_genre) {
    lines.push(`Main Genre: ${scorecard.main_genre}`);
  }
  if (scorecard?.logline) {
    lines.push(`Logline: ${scorecard.logline}`);
  }
  lines.push('');

  if (scorecard) {
    lines.push(`Engagement Score: ${Math.round(scorecard.overall_engagement_score)}/100`);
    lines.push('');
    lines.push('Score Breakdown:');
    if (useDeterministicScores && vs) {
      const ss = vs.story_spine_pct ?? scorecard.story_spine_pct;
      const cs = vs.character_strength_pct ?? scorecard.character_strength_pct;
      const ts = vs.theme_strength_pct ?? scorecard.theme_strength_pct;
      const cv = vs.commercial_viability_pct ?? scorecard.commercial_viability_pct;
      lines.push('  Story Spine');
      lines.push(`    Story Spine: ${fmtPct(ss)}`);
      lines.push(`    Story Strength: ${fmtPct(vs.etb_structure_pct)}`);
      lines.push(`    Pacing: ${fmtPct(vs.pacing_pct)}`);
      lines.push(`    Story Beat Strength: ${fmtPct(vs.story_beat_strength_pct)}`);
      lines.push(`    Scene Strength: ${fmtPct(vs.scene_strength_pct ?? scorecard.scene_strength)}`);
      lines.push(`    Concentration of Overarching Problem: ${fmtPct(vs.concentration_pct)}`);
      lines.push('  Character Strength');
      lines.push(`    Character Strength: ${fmtPct(cs)}`);
      lines.push(`    Character Concentration: ${fmtPct(vs.character_concentration_pct)}`);
      lines.push(`    Character Resonance: ${fmtPct(vs.character_resonance_pct)}`);
      lines.push(`    Character Active Metre: ${fmtPct(vs.character_active_pct)}`);
      lines.push(`    Dialogue Strength: ${fmtPct(vs.dialogue_strength_pct ?? scorecard.dialogue_strength)}`);
      lines.push(`    Character Journey: ${fmtPct(vs.character_journey_pct)}`);
      lines.push('  Theme Strength');
      lines.push(`    Theme Strength: ${fmtPct(ts)}`);
      lines.push(`    Thematic Quality: ${fmtPct(vs.thematic_quality_pct ?? ts)}`);
      lines.push(`    Authenticity Score: ${fmtPct(vs.authenticity_pct)}`);
      lines.push(`    Genre Promise: ${fmtPct(vs.genre_promise_pct)}`);
      lines.push('  Commercial Viability');
      lines.push(`    Commercial Viability: ${fmtPct(cv)}`);
      lines.push(
        `    Originality Metre: ${
          scorecard && isOriginalityExcluded(scorecard)
            ? formatOriginalityDisplay(scorecard)
            : fmtPct(vs.originality_pct)
        }`,
      );
      lines.push(`    Target Market: ${fmtPct(vs.target_market_pct)}`);
      const originalityNote = scorecard ? originalityExclusionNotice(scorecard) : null;
      if (originalityNote) {
        lines.push('');
        lines.push('Originality flag:');
        lines.push(originalityNote);
      }
    } else if (isCreator) {
      lines.push(`  Story Spine: ${Math.round((scorecard.etb_structure?.raw_score ?? 0) * 10)}%`);
      lines.push(`  Content Pacing: ${scorecard.genre_affect ? Math.round((scorecard.genre_affect.raw_score ?? 0) * 10) : Math.round(scorecard.engagement_percentage ?? 0)}%`);
      lines.push(`  General Thematic Clarity: ${Math.round((scorecard.conceptual_relevance?.raw_score ?? 0) * 10)}%`);
      lines.push(`  Authenticity Score: ${Math.round((scorecard.conceptual_relevance?.raw_score ?? 0) * 10)}%`);
      lines.push(`  Character Resonance: ${Math.round((scorecard.emotional_transformation?.raw_score ?? 0) * 10)}%`);
      lines.push(`  Genre Accuracy: ${scorecard.genre_affect ? Math.round((scorecard.genre_affect.raw_score ?? 0) * 10) : Math.round(scorecard.engagement_percentage ?? 0)}%`);
      if (typeof scorecard.scene_strength === 'number') {
        lines.push(`  Scene Strength: ${Math.round(scorecard.scene_strength)}%`);
      }
      if (typeof scorecard.dialogue_strength === 'number') {
        lines.push(`  Dialogue: ${Math.round(scorecard.dialogue_strength)}%`);
      }
    } else {
      lines.push(`  ETB Structure: ${scorecard.etb_structure?.raw_score ?? 0}/10`);
      lines.push(`  Content Pacing: ${scorecard.engagement_percentage}%`);
      lines.push(`  Thematic Clarity: ${scorecard.conceptual_relevance?.raw_score ?? 0}/10`);
      lines.push(`  Character Resonance: ${scorecard.emotional_transformation?.raw_score ?? 0}/10`);
      lines.push(`  Genre: ${scorecard.genre_affect?.raw_score ?? 0}/10`);
    }
    lines.push('');
  }

  // Detailed Analysis
  const state = results as Record<string, unknown>;
  const etbAnalysis = (state.etb_analysis ?? results.etb_analysis) as string | undefined;
  const conceptualAnalysis = (state.conceptual_analysis ?? results.conceptual_analysis) as string | undefined;
  const emotionalAnalysis = (state.emotional_analysis ?? results.emotional_analysis) as string | undefined;
  const genreAnalysis = (state.genre_analysis ?? results.genre_analysis) as string | undefined;
  const sceneAnalysis = (state.scene_analysis ?? results.scene_analysis) as string | undefined;
  const dialogueAnalysis = (state.dialogue_analysis ?? results.dialogue_analysis) as string | undefined;

  const { contentPacing, genreAccuracy } = splitGenreAnalysis(genreAnalysis);

  lines.push('DETAILED ANALYSIS');
  lines.push('-'.repeat(50));
  lines.push('');

  if (isCreator) {
    if (etbAnalysis) {
      lines.push('STORY SPINE');
      lines.push(stripBold(etbAnalysis));
      lines.push('');
    }
    if (contentPacing) {
      lines.push('CONTENT PACING');
      lines.push(contentPacing);
      lines.push('');
    }
    if (conceptualAnalysis) {
      lines.push('THEMATIC CLARITY');
      lines.push(stripBold(conceptualAnalysis));
      lines.push('');
    }
    if (emotionalAnalysis) {
      lines.push('CHARACTER RESONANCE');
      lines.push(stripBold(emotionalAnalysis));
      lines.push('');
    }
    if (genreAccuracy) {
      lines.push('GENRE ACCURACY');
      lines.push(genreAccuracy);
      lines.push('');
    }
    if (sceneAnalysis) {
      lines.push('SCENE STRENGTH');
      lines.push(stripBold(sceneAnalysis));
      lines.push('');
    }
    if (dialogueAnalysis) {
      lines.push('DIALOGUE');
      lines.push(stripBold(dialogueAnalysis));
      lines.push('');
    }
  } else {
    if (etbAnalysis) {
      lines.push('ETB STRUCTURE');
      lines.push(stripBold(etbAnalysis));
      lines.push('');
    }
    if (genreAnalysis) {
      lines.push('GENRE & AFFECT');
      lines.push(stripBold(genreAnalysis));
      lines.push('');
    }
    if (conceptualAnalysis) {
      lines.push('CONCEPTUAL RELEVANCE');
      lines.push(stripBold(conceptualAnalysis));
      lines.push('');
    }
    if (emotionalAnalysis) {
      lines.push('EMOTIONAL TRANSFORMATION');
      lines.push(stripBold(emotionalAnalysis));
      lines.push('');
    }
  }

  // Blueprint
  const blueprint = results.blueprint;
  if (blueprint) {
    if (isCreator && (blueprint.identified_pitfalls?.length > 0 || blueprint.action_steps?.length > 0)) {
      lines.push('IMPROVEMENT BLUEPRINT');
      lines.push('-'.repeat(50));
      lines.push('');
      if (blueprint.identified_pitfalls?.length) {
        lines.push('Identified Pitfalls:');
        blueprint.identified_pitfalls.forEach((p) => {
          lines.push(`  [${p.severity || 'General'}] ${p.category}: ${p.issue}`);
        });
        lines.push('');
      }
      if (blueprint.action_steps?.length) {
        lines.push('Action Steps:');
        blueprint.action_steps.forEach((step, i) => {
          lines.push(`  ${i + 1}. ${step}`);
        });
        lines.push('');
      }
    }
    if (!isCreator && blueprint.commission_summary) {
      lines.push('COMMISSION SUMMARY');
      lines.push('-'.repeat(50));
      lines.push('');
      lines.push(blueprint.commission_summary);
      lines.push('');
    }
    if (isCreator && blueprint.encouraging_quote) {
      lines.push('');
      lines.push(blueprint.encouraging_quote);
    }
  }

  lines.push('');
  lines.push('Generated by Nuclius Index');

  return lines.join('\n');
}

/** Trigger download of a text file. */
export function downloadAsText(
  results: AnalysisResults,
  filename?: string,
  options: NucliusExportOptions = {},
): void {
  const text = buildScorecardText(results, options);
  const safeName = (filename || results.script_title || 'scorecard')?.replace(/[^a-zA-Z0-9-_]/g, '_') || 'scorecard';
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${safeName}_scorecard.txt`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Build Word document and trigger download. */
export async function downloadAsDocx(
  results: AnalysisResults,
  filename?: string,
  options: NucliusExportOptions = {},
): Promise<void> {
  const nucliusText = buildNucliusScorecardText(results, options);
  const blueprintMd =
    nucliusText.trim() ||
    results.blueprint_markdown ||
    (results.blueprint && typeof results.blueprint === 'object' && 'blueprint_markdown' in results.blueprint
      ? (results.blueprint as { blueprint_markdown?: string }).blueprint_markdown
      : undefined);

  if (typeof blueprintMd === 'string' && blueprintMd.trim()) {
    const blueprintParas: Paragraph[] = [];
    const lines = blueprintMd.replace(/\r\n/g, '\n').split('\n');
    for (const raw of lines) {
      const t = raw.trimEnd();
      const trimmed = t.trim();
      if (trimmed === '') {
        blueprintParas.push(new Paragraph({ children: [new TextRun(' ')], spacing: { after: 60 } }));
        continue;
      }
      if (trimmed.startsWith('# ')) {
        blueprintParas.push(
          new Paragraph({
            children: [new TextRun({ text: stripBold(trimmed.replace(/^#+\s*/, '')), bold: true, size: 32 })],
            heading: HeadingLevel.HEADING_1,
            spacing: { after: 120 },
          })
        );
        continue;
      }
      if (/^##\s/.test(trimmed)) {
        blueprintParas.push(
          new Paragraph({
            children: [new TextRun({ text: stripBold(trimmed.replace(/^#+\s*/, '')), bold: true, size: 28 })],
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 160, after: 100 },
          })
        );
        continue;
      }
      if (trimmed.startsWith('- ')) {
        blueprintParas.push(
          new Paragraph({
            children: [new TextRun(`• ${stripBold(trimmed.slice(2))}`)],
            spacing: { after: 80 },
          })
        );
        continue;
      }
      blueprintParas.push(
        new Paragraph({
          children: [new TextRun(stripBold(trimmed))],
          spacing: { after: 80 },
        })
      );
    }
    blueprintParas.push(
      new Paragraph({
        children: [new TextRun({ text: 'Generated by Nuclius Index', size: 18 })],
        spacing: { before: 240 },
        alignment: AlignmentType.CENTER,
      })
    );
    const doc = new Document({
      sections: [
        {
          properties: {
            page: {
              margin: {
                top: convertInchesToTwip(0.75),
                right: convertInchesToTwip(0.75),
                bottom: convertInchesToTwip(0.75),
                left: convertInchesToTwip(0.75),
              },
            },
          },
          children: blueprintParas,
        },
      ],
    });
    const blob = await Packer.toBlob(doc);
    const safeName = (filename || results.script_title || 'scorecard')?.replace(/[^a-zA-Z0-9-_]/g, '_') || 'scorecard';
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${safeName}_scorecard.docx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    return;
  }

  const scorecard = results.scorecard;
  const isCreator = results.audience_type === 'creator';
  const scorecardTitle = 'SCRIPT ANALYSIS RESULT';
  const useDeterministicScores = scorecard ? hasDeterministicPillarScores(scorecard) : false;
  const vs = scorecard?.v7_scores ?? scorecard?.v6_scores;

  const state = results as Record<string, unknown>;
  const etbAnalysis = (state.etb_analysis ?? results.etb_analysis) as string | undefined;
  const conceptualAnalysis = (state.conceptual_analysis ?? results.conceptual_analysis) as string | undefined;
  const emotionalAnalysis = (state.emotional_analysis ?? results.emotional_analysis) as string | undefined;
  const genreAnalysis = (state.genre_analysis ?? results.genre_analysis) as string | undefined;
  const sceneAnalysis = (state.scene_analysis ?? results.scene_analysis) as string | undefined;
  const dialogueAnalysis = (state.dialogue_analysis ?? results.dialogue_analysis) as string | undefined;

  const { contentPacing, genreAccuracy } = splitGenreAnalysis(genreAnalysis);

  const toParagraphs = (text: string | undefined): Paragraph[] => {
    if (!text || typeof text !== 'string') return [];
    return text.split('\n').map((line) => {
      const stripped = stripBold(line);
      return new Paragraph({
        children: [new TextRun(stripped || ' ')],
        spacing: { after: 80 },
      });
    });
  };

  const header = (title: string): Paragraph =>
    new Paragraph({
      children: [new TextRun({ text: title, bold: true, size: 28 })],
      heading: HeadingLevel.HEADING_2,
      spacing: { before: 240, after: 120 },
    });

  const children: Paragraph[] = [
    new Paragraph({
      children: [new TextRun({ text: scorecardTitle, bold: true, size: 32 })],
      heading: HeadingLevel.HEADING_1,
      alignment: AlignmentType.CENTER,
      spacing: { after: 200 },
    }),
    new Paragraph({
      children: [new TextRun({ text: results.script_title || 'Script Analysis', bold: true })],
      spacing: { after: 200 },
    }),
  ];

  if (scorecard?.main_genre) {
    children.push(
      new Paragraph({
        children: [new TextRun(`Main Genre: ${scorecard.main_genre}`)],
        spacing: { after: 200 },
      })
    );
  }

  if (scorecard?.logline) {
    children.push(
      new Paragraph({
        children: [new TextRun({ text: scorecard.logline, italics: true })],
        spacing: { after: 200 },
      })
    );
  }

  if (scorecard) {
    children.push(
      new Paragraph({
        children: [
          new TextRun({
            text: `Engagement Score: ${Math.round(scorecard.overall_engagement_score)}/100`,
            bold: true,
          }),
        ],
        spacing: { after: 120 },
      })
    );
    children.push(header('Score Breakdown'));
    if (useDeterministicScores && vs) {
      const ss = vs.story_spine_pct ?? scorecard.story_spine_pct;
      const cs = vs.character_strength_pct ?? scorecard.character_strength_pct;
      const ts = vs.theme_strength_pct ?? scorecard.theme_strength_pct;
      const cv = vs.commercial_viability_pct ?? scorecard.commercial_viability_pct;
      [
        `Story Spine: ${fmtPct(ss)}`,
        `  Story Strength: ${fmtPct(vs.etb_structure_pct)}`,
        `  Pacing: ${fmtPct(vs.pacing_pct)}`,
        `  Story Beat Strength: ${fmtPct(vs.story_beat_strength_pct)}`,
        `  Scene Strength: ${fmtPct(vs.scene_strength_pct ?? scorecard.scene_strength)}`,
        `  Concentration: ${fmtPct(vs.concentration_pct)}`,
        `Character Strength: ${fmtPct(cs)}`,
        `  Character Concentration: ${fmtPct(vs.character_concentration_pct)}`,
        `  Character Resonance: ${fmtPct(vs.character_resonance_pct)}`,
        `  Character Active Metre: ${fmtPct(vs.character_active_pct)}`,
        `  Dialogue Strength: ${fmtPct(vs.dialogue_strength_pct ?? scorecard.dialogue_strength)}`,
        `  Character Journey: ${fmtPct(vs.character_journey_pct)}`,
        `Theme Strength: ${fmtPct(ts)}`,
        `  Thematic Quality: ${fmtPct(vs.thematic_quality_pct ?? ts)}`,
        `  Authenticity Score: ${fmtPct(vs.authenticity_pct)}`,
        `  Genre Promise: ${fmtPct(vs.genre_promise_pct)}`,
        `Commercial Viability: ${fmtPct(cv)}`,
        `  Originality Metre: ${
          scorecard && isOriginalityExcluded(scorecard)
            ? formatOriginalityDisplay(scorecard)
            : fmtPct(vs.originality_pct)
        }`,
        `  Target Market: ${fmtPct(vs.target_market_pct)}`,
      ].forEach((s) => children.push(new Paragraph({ children: [new TextRun(s)], spacing: { after: 80 } })));
    } else if (isCreator) {
      children.push(
        ...[
          `Story Spine: ${Math.round((scorecard.etb_structure?.raw_score ?? 0) * 10)}%`,
          `Content Pacing: ${scorecard.genre_affect ? Math.round((scorecard.genre_affect.raw_score ?? 0) * 10) : Math.round(scorecard.engagement_percentage ?? 0)}%`,
          `General Thematic Clarity: ${Math.round((scorecard.conceptual_relevance?.raw_score ?? 0) * 10)}%`,
          `Authenticity Score: ${Math.round((scorecard.conceptual_relevance?.raw_score ?? 0) * 10)}%`,
          `Character Resonance: ${Math.round((scorecard.emotional_transformation?.raw_score ?? 0) * 10)}%`,
          `Genre Accuracy: ${scorecard.genre_affect ? Math.round((scorecard.genre_affect.raw_score ?? 0) * 10) : Math.round(scorecard.engagement_percentage ?? 0)}%`,
          ...(typeof scorecard.scene_strength === 'number' ? [`Scene Strength: ${Math.round(scorecard.scene_strength)}%`] : []),
          ...(typeof scorecard.dialogue_strength === 'number' ? [`Dialogue: ${Math.round(scorecard.dialogue_strength)}%`] : []),
        ].map((s) => new Paragraph({ children: [new TextRun(s)], spacing: { after: 80 } }))
      );
    } else {
      children.push(
        ...[
          `ETB Structure: ${scorecard.etb_structure?.raw_score ?? 0}/10`,
          `Content Pacing: ${scorecard.engagement_percentage}%`,
          `Thematic Clarity: ${scorecard.conceptual_relevance?.raw_score ?? 0}/10`,
          `Character Resonance: ${scorecard.emotional_transformation?.raw_score ?? 0}/10`,
          `Genre: ${scorecard.genre_affect?.raw_score ?? 0}/10`,
        ].map((s) => new Paragraph({ children: [new TextRun(s)], spacing: { after: 80 } }))
      );
    }
  }

  children.push(header('Detailed Analysis'));

  if (isCreator) {
    if (etbAnalysis) {
      children.push(header('Story Spine'));

      children.push(...toParagraphs(etbAnalysis));
    }
    if (contentPacing) {
      children.push(header('Content Pacing'));

      children.push(...toParagraphs(contentPacing));
    }
    if (conceptualAnalysis) {
      children.push(header('Thematic Clarity'));

      children.push(...toParagraphs(conceptualAnalysis));
    }
    if (emotionalAnalysis) {
      children.push(header('Character Resonance'));

      children.push(...toParagraphs(emotionalAnalysis));
    }
    if (genreAccuracy) {
      children.push(header('Genre Accuracy'));

      children.push(...toParagraphs(genreAccuracy));
    }
    if (sceneAnalysis) {
      children.push(header('Scene Strength'));

      children.push(...toParagraphs(sceneAnalysis));
    }
    if (dialogueAnalysis) {
      children.push(header('Dialogue'));

      children.push(...toParagraphs(dialogueAnalysis));
    }
  } else {
    if (etbAnalysis) {
      children.push(header('ETB Structure'));

      children.push(...toParagraphs(etbAnalysis));
    }
    if (genreAnalysis) {
      children.push(header('Genre & Affect'));

      children.push(...toParagraphs(genreAnalysis));
    }
    if (conceptualAnalysis) {
      children.push(header('Conceptual Relevance'));

      children.push(...toParagraphs(conceptualAnalysis));
    }
    if (emotionalAnalysis) {
      children.push(header('Emotional Transformation'));

      children.push(...toParagraphs(emotionalAnalysis));
    }
  }

  const blueprint = results.blueprint;
  if (blueprint) {
    if (isCreator && (blueprint.identified_pitfalls?.length > 0 || blueprint.action_steps?.length > 0)) {
      children.push(header('Improvement Blueprint'));

      if (blueprint.identified_pitfalls?.length) {
        children.push(
          new Paragraph({
            children: [new TextRun({ text: 'Identified Pitfalls', bold: true })],
            spacing: { before: 120, after: 80 },
          })
        );
        blueprint.identified_pitfalls.forEach((p) => {
          children.push(
            new Paragraph({
              children: [
                new TextRun({ text: `[${p.severity || 'General'}] ${p.category}: `, bold: true }),
                new TextRun(p.issue),
              ],
              spacing: { after: 80 },
            })
          );
        });
      }
      if (blueprint.action_steps?.length) {
        children.push(
          new Paragraph({
            children: [new TextRun({ text: 'Action Steps', bold: true })],
            spacing: { before: 120, after: 80 },
          })
        );
        blueprint.action_steps.forEach((step, i) => {
          children.push(
            new Paragraph({
              children: [new TextRun(`${i + 1}. ${step}`)],
              spacing: { after: 80 },
            })
          );
        });
      }
    }
    if (!isCreator && blueprint.commission_summary) {
      children.push(header('Commission Summary'));

      children.push(...toParagraphs(blueprint.commission_summary));
    }
    if (isCreator && blueprint.encouraging_quote) {
      children.push(
        new Paragraph({
          children: [new TextRun({ text: blueprint.encouraging_quote, italics: true })],
          spacing: { before: 240, after: 80 },
        })
      );
    }
  }

  children.push(
    new Paragraph({
      children: [new TextRun({ text: 'Generated by Nuclius Index', size: 18 })],
      spacing: { before: 240 },
      alignment: AlignmentType.CENTER,
    })
  );

  const doc = new Document({
    sections: [
      {
        properties: {
          page: {
            margin: {
              top: convertInchesToTwip(0.75),
              right: convertInchesToTwip(0.75),
              bottom: convertInchesToTwip(0.75),
              left: convertInchesToTwip(0.75),
            },
          },
        },
        children,
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  const safeName = (filename || results.script_title || 'scorecard')?.replace(/[^a-zA-Z0-9-_]/g, '_') || 'scorecard';
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${safeName}_scorecard.docx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
